import { useCallback, useId, useRef, type JSX, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { DUCK_COLORS, type DuckColor } from '@duck/contracts';

/**
 * M0 "First Breath" — the instant, freely-reversible duck pick. Choosing a duck is IMMEDIATE and
 * changeable as many times as you like before entering: clicking a swatch updates the live
 * {@link DuckPreview} on the spot, with no confirm dialog and no commit friction. Delight + obvious
 * reversibility is the goal (Wonder, then a playful "ooh, try them all").
 *
 * ACCESSIBILITY is first-class here:
 *  • The swatches form a single ARIA `radiogroup` (one tab stop; ←/→/↑/↓ move between options, Home/
 *    End jump to the ends), so the picker is fully keyboard-reachable.
 *  • Selection is DUAL-CODED beyond colour: the chosen swatch gets a ring + a ✓ check mark, and the
 *    selected colour's NAME is shown as live text ("Selected: Green") — never colour alone. Each
 *    swatch also carries its colour name as its accessible label.
 *  • A `prefers-reduced-motion` gate is honoured by framer-motion's `useReducedMotion` (the preview's
 *    swap/bob is suppressed); the swatches themselves never rely on motion to convey state.
 */

const SWATCH: Record<DuckColor, string> = {
  yellow: '#ffd23f',
  white: '#f4f4f8',
  brown: '#b5793a',
  green: '#6fcf5f',
  blue: '#5aa9e6',
  pink: '#ff9ecb',
  black: '#3a3a44',
  // Art revision 2026-06-22 — nine more tints (kept in sync with render DUCK_HEX).
  orange: '#ff924c',
  red: '#ed5a5a',
  peach: '#ffc6a3',
  lavender: '#c6b6f0',
  purple: '#a977e0',
  teal: '#3fc7c2',
  mint: '#9be3c0',
  cream: '#f5e6c8',
  grey: '#9aa3b0',
};

/** Title-case colour name for the dual-coded text read-out + accessible labels. */
function label(color: DuckColor): string {
  return color.charAt(0).toUpperCase() + color.slice(1);
}

export function DuckPicker({
  value,
  onChange,
}: {
  value: DuckColor;
  onChange: (color: DuckColor) => void;
}): JSX.Element {
  const groupLabelId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // Roving-focus arrow-key navigation across the radiogroup. Selecting also moves focus so the
  // keyboard and pointer paths feel identical (and it's obvious which swatch is active).
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      const last = DUCK_COLORS.length - 1;
      let next = index;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return; // let Space/Enter activate the focused radio natively
      }
      e.preventDefault();
      onChange(DUCK_COLORS[next]!);
      refs.current[next]?.focus();
    },
    [onChange],
  );

  return (
    <div className="mb-4">
      <span id={groupLabelId} className="mb-1.5 block text-sm font-medium text-white/70">
        Pick a colour
      </span>
      <div
        role="radiogroup"
        aria-labelledby={groupLabelId}
        className="flex flex-wrap items-center gap-2"
      >
        {DUCK_COLORS.map((color, index) => {
          const selected = value === color;
          return (
            <button
              key={color}
              ref={(el) => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label(color)}
              // Roving tabindex: only the selected swatch is in the tab order.
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(color)}
              onKeyDown={(e) => onKeyDown(e, index)}
              style={{ background: SWATCH[color] }}
              className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 outline-none transition focus-visible:ring-2 focus-visible:ring-amber-300/70 ${
                selected ? 'scale-110 border-amber-300' : 'border-white/20 hover:border-white/50'
              }`}
            >
              {/* Dual-code selection beyond colour: a ✓ check on the chosen swatch. */}
              {selected && (
                <span
                  aria-hidden="true"
                  className="text-[15px] font-black leading-none text-slate-900 drop-shadow"
                >
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Dual-coded TEXT read-out of the current choice (never colour-only). Announced politely. */}
      <p aria-live="polite" className="mt-2 text-xs text-white/60">
        Selected: <span className="font-semibold text-white/85">{label(value)}</span> · pick any, as
        many times as you like
      </p>
    </div>
  );
}

/**
 * A live, instant preview of the chosen duck (SVG, so the swap is immediate with zero Pixi cost). It
 * sits at the top of the entry form so a colour click is felt right away — the duck you'll BE. Under
 * reduced motion the gentle entrance/bob is suppressed; the duck simply appears in its new colour.
 */
export function DuckPreview({ color }: { color: DuckColor }): JSX.Element {
  // framer-motion's reactive hook — honours `prefers-reduced-motion` (and OS toggles mid-session) so
  // the preview's entrance/bob is suppressed for users who ask for less motion.
  const reduce = useReducedMotion();
  const body = SWATCH[color];
  const belly = '#ffffff';
  return (
    <div
      className="mx-auto mb-4 flex h-24 w-24 items-center justify-center"
      data-duck-preview={color}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.svg
          key={color}
          viewBox="0 0 96 96"
          className="h-24 w-24"
          role="img"
          aria-label={`Preview of your ${label(color)} duck`}
          initial={reduce ? false : { scale: 0.7, opacity: 0, rotate: -6 }}
          animate={
            reduce
              ? { scale: 1, opacity: 1 }
              : { scale: 1, opacity: 1, rotate: 0, y: [0, -3, 0] }
          }
          exit={reduce ? { opacity: 0 } : { scale: 0.7, opacity: 0, rotate: 6 }}
          transition={reduce ? { duration: 0.12 } : { duration: 0.45, ease: 'easeOut' }}
        >
          {/* shadow */}
          <ellipse cx="48" cy="80" rx="26" ry="7" fill="rgba(0,0,0,0.22)" />
          {/* body + belly */}
          <ellipse cx="46" cy="52" rx="30" ry="24" fill={body} />
          <ellipse cx="49" cy="58" rx="20" ry="15" fill={belly} opacity="0.18" />
          {/* head + eye + beak */}
          <circle cx="66" cy="34" r="16" fill={body} />
          <circle cx="71" cy="31" r="3.2" fill="#141821" />
          <polygon points="80,34 95,38 80,42" fill="#ff9f1c" />
        </motion.svg>
      </AnimatePresence>
    </div>
  );
}
